import styled from '@emotion/styled';
import { useContext } from 'react';
import { companyUserContext } from 'application/contexts/useCompanyUser';
import { companyChatContext } from 'application/contexts/useCompanyChatRoom';
import { UserIcon } from 'ui/components/elements/UserIcon/UserIcon';
import { Message } from './Message';
import { MessageInput } from 'ui/components/features/MessageInput/MessageInput';
import { SetMeetingDate } from 'ui/components/screens/modals/SetMeetingDate/SetMeetingDate';
import { useSetMeeting } from './useSetMeeting';
import IconCalendar from 'ui/images/icons/calendar.svg';
import { SnackBar } from 'ui/components/elements/SnackBar/SnackBar';
import { ArchiveAgentModal } from 'ui/components/screens/modals/ArchiveAgentModal/ArchiveAgentModal';
import dayjs from 'dayjs';
import { mqUser } from 'utils/responsive';
import { getFullUserName } from 'utils/string';

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 0 10px;
    position: relative;
    height: 100%;
    background: var(--white);

    ${mqUser('SP')} {
        padding: 0;
    }
`;

const AgentNameContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;
    font-weight: var(--font-bold);
    font-size: var(--font-x-large);

    ${mqUser('SP')} {
        flex-direction: column;
        align-items: flex-start;
        font-size: 0.9rem;
        gap: 0px;
    }
`;

const DateAndMeetingContainer = styled.div`
    display: flex;
    align-items: center;
    margin-left: auto;
    gap: 20px;

    ${mqUser('SP')} {
        padding-left: 75px;
        width: 100%;
        gap: 8px;
    }
`;

const BackButton = styled.button`
    display: none;
    align-items: center;
    background: none;
    border: none;
    padding: 10px;
    cursor: pointer;
    color: var(--main-color-blue);
    font-size: var(--font-medium);
    font-weight: var(--font-bold);
    margin-right: auto;
    margin-top: -25px;

    ${mqUser('SP')} {
        display: block;
    }

    &::before {
        content: '←';
        margin-right: 5px;
        font-size: 20px;
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    align-items: center;
    margin-left: auto;
    gap: 10px;
    font-weight: var(--font-bold);
    font-size: var(--font-x-large);

    ${mqUser('SP')} {
        font-size: 0.8rem;
    }
`;

const DateContainer = styled.div`
    font-size: var(--font-small);
    display: flex;
    flex-direction: column;
    gap: 5px;

    ${mqUser('SP')} {
        padding-top: 5px;
    }
`;

const FixedTop = styled.div`
    width: 100%;
    padding: 10px;
    background: var(--white);
    box-shadow: 0px 8px 8px -5px rgba(0, 0, 0, 0.1);

    ${mqUser('SP')} {
        padding: 15px 5px;
    }
`;

const MessageContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    height: calc(100% - 200px);
    overflow-y: auto;
    background: var(--white);
    padding: 20px 0;
    position: relative;

    ${mqUser('SP')} {
        height: calc(100% - 170px);
    }
`;

const MeetingDateLabel = styled.div`
    position: absolute;
    top: 5px;
    right: 20px;
    background-color: #555555;
    color: var(--white);
    padding: 8px 16px;
    border-radius: 15px;
    font-weight: 700;
    font-size: 12px;
    z-index: 1;

    ${mqUser('SP')} {
        right: 22px;
        padding: 6px 12px;
    }
`;

const MessageInputContainer = styled.div`
    position: absolute;
    bottom: 0;
    left: 10px;
    width: calc(100% - 20px);

    ${mqUser('SP')} {
        position: fixed;
        bottom: 10px;
    }
`;

const CalenderContainer = styled.div`
    display: flex;
    align-items: center;
    margin-right: 10px;
    margin-left: auto;
    cursor: pointer;
    background: var(--primary);
    color: var(--white);
    justify-content: space-between;
    border-radius: 4px;
    padding: 8px 12px;
    gap: 4px;
    font-weight: 700;
    font-size: 14px;
    white-space: nowrap;

    img {
        width: 18px;
        height: 18px;
    }

    ${mqUser('SP')} {
        font-size: 0.8rem;
        padding: 4px 8px;

        img {
            display: none;
        }
    }
`;

const ArchiveContainer = styled.div`
    display: flex;
    align-items: center;
    margin-right: 10px;
    margin-left: auto;
    cursor: pointer;
    border: 2px solid var(--warn);
    color: var(--warn);
    justify-content: space-between;
    border-radius: 4px;
    padding: 6px 10px;
    gap: 4px;
    font-weight: 700;
    font-size: 14px;
    white-space: nowrap;

    &:hover {
        background: var(--warn);
        color: var(--white);
    }
    transition:
        background 0.2s,
        color 0.2s;

    ${mqUser('SP')} {
        font-size: 0.8rem;
        padding: 4px 8px;
    }
`;

const MeetingDateConfirmationContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    background-color: #f5f7fa;
    border-left: 4px solid var(--primary);
    padding: 16px;
    border-radius: 8px;
    margin-bottom: 20px;
`;

const MeetingDateConfirmationLabel = styled.div`
    font-size: var(--font-medium);
    color: #333;
    font-weight: 600;
`;

const MeetingDateButtonGroup = styled.div`
    display: flex;
    gap: 12px;
`;

const MeetingDateButton = styled.button`
    padding: 8px 16px;
    font-size: 14px;
    font-weight: 600;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    background-color: var(--primary);
    color: var(--white);
    transition: background-color 0.2s;

    &:hover {
        background-color: #f29460;
    }

    &:nth-of-type(2) {
        background-color: #dee2e6;
        color: #333;

        &:hover {
            background-color: #ced4da;
        }
    }
`;

const IconContainer = styled.div`
    display: flex;
    ${mqUser('SP')} {
        display: none;z
    }
`;

const CompanyName = styled.div`
    ${mqUser('SP')} {
        padding-left: 75px;
    }
`;

interface ChatRoomProps {
    onBack?: () => void;
}

export const ChatRoom: React.FC<ChatRoomProps> = ({ onBack }) => {
    const {
        loading,
        selectedChatRoom,
        message,
        snackbarMessage,
        blockAgentErrors,
        setMessage,
        sendMessage,
        chatMessages,
        setMeetingDate,
        confirmMeetingDate,
        declineMeetingDate,
        showSnackbar,
        setShowSnackbar,
        createArchiveAgentModal,
        openArchiveAgentModal,
        blockAgent,
        setArchiveAgentValue,
    } = useContext(companyChatContext);
    const { user } = useContext(companyUserContext);

    const { control, errors, handleSubmit, openModal, createModal, closeModal } = useSetMeeting();

    if (!selectedChatRoom || !user) {
        return null;
    }

    // マッチング日から2週間が経過かつ、面談日が未設定
    const needRemindMessage = !!(
        selectedChatRoom.match.date_matched &&
        dayjs().diff(selectedChatRoom.match.date_matched, 'day') >= 14 &&
        !selectedChatRoom.match.meeting_date
    );

    const showMeetingDateConfirmation =
        selectedChatRoom.match?.meeting_date_confirmation?.tentative_meeting_date &&
        selectedChatRoom.match?.meeting_date_confirmation?.requesting_agent_user;

    const waitingMeetingDateConfirmation =
        selectedChatRoom.match?.meeting_date_confirmation?.tentative_meeting_date &&
        !selectedChatRoom.match?.meeting_date_confirmation?.requesting_agent_user;

    return (
        <Wrapper>
            <SnackBar message={snackbarMessage} open={showSnackbar} setOpen={setShowSnackbar} />
            <FixedTop>
                <AgentNameContainer>
                    <IconContainer>
                        <UserIcon size={40} src={selectedChatRoom.match.agent.icon_image} />
                    </IconContainer>
                    <CompanyName
                        onClick={() => {
                            window.open(`/company/agent_profile/${selectedChatRoom.match.agent.id}/`, '_blank');
                        }}
                    >
                        {selectedChatRoom.match.agent.name}
                    </CompanyName>
                    <DateAndMeetingContainer>
                        <DateContainer>
                            <span>マッチング日：{dayjs(selectedChatRoom.match.date_matched).format('YYYY/MM/DD')}</span>
                            <span>
                                面談日：
                                {selectedChatRoom.match.meeting_date
                                    ? dayjs(selectedChatRoom.match.meeting_date).format('YYYY/MM/DD HH:mm')
                                    : '未設定'}
                            </span>
                        </DateContainer>

                        <ButtonContainer>
                            {!showMeetingDateConfirmation && (
                                <CalenderContainer onClick={() => openModal()}>
                                    面談設定
                                    <img src={IconCalendar} alt="面談日を決める" />
                                </CalenderContainer>
                            )}
                            {!selectedChatRoom.match.archived && (
                                <ArchiveContainer onClick={() => openArchiveAgentModal()}>通報する</ArchiveContainer>
                            )}
                        </ButtonContainer>
                    </DateAndMeetingContainer>
                </AgentNameContainer>
            </FixedTop>

            <MessageContainer>
                {onBack && <BackButton onClick={onBack}>戻る</BackButton>}
                {selectedChatRoom.match.meeting_date && (
                    <MeetingDateLabel>
                        {dayjs(selectedChatRoom.match.meeting_date).format('YYYY/MM/DD HH:mm')} 面談予定
                    </MeetingDateLabel>
                )}
                {showMeetingDateConfirmation && (
                    <MeetingDateConfirmationContainer>
                        <MeetingDateConfirmationLabel>
                            {`お相手が面談日時を${dayjs(
                                selectedChatRoom.match?.meeting_date_confirmation?.tentative_meeting_date,
                            ).format('YYYY/MM/DD HH:mm')}に設定しました。`}
                        </MeetingDateConfirmationLabel>
                        <MeetingDateButtonGroup>
                            <MeetingDateButton disabled={loading} onClick={() => confirmMeetingDate()}>
                                了承する
                            </MeetingDateButton>
                            <MeetingDateButton disabled={loading} onClick={() => declineMeetingDate()}>
                                断る
                            </MeetingDateButton>
                        </MeetingDateButtonGroup>
                    </MeetingDateConfirmationContainer>
                )}
                {waitingMeetingDateConfirmation && (
                    <MeetingDateConfirmationContainer>
                        <MeetingDateConfirmationLabel>
                            {`${dayjs(selectedChatRoom.match?.meeting_date_confirmation?.tentative_meeting_date).format(
                                'YYYY/MM/DD HH:mm',
                            )} にて面談リクエスト中です。お相手の承諾をお待ちください。`}
                        </MeetingDateConfirmationLabel>
                    </MeetingDateConfirmationContainer>
                )}
                {selectedChatRoom.match.message_from_brokerage && (
                    <Message
                        icon_image={selectedChatRoom.match.from_agent_user.icon_image}
                        name={getFullUserName(
                            selectedChatRoom.match.from_agent_user.last_name,
                            selectedChatRoom.match.from_agent_user.first_name,
                        )}
                        isSelf={false}
                        date={selectedChatRoom.match.date_matched}
                        message={selectedChatRoom.match.message_from_brokerage}
                    />
                )}
                {chatMessages.map((chat) => (
                    <Message
                        key={chat.id}
                        icon_image={
                            chat.sender_agent_user
                                ? chat.sender_agent_user?.icon_image || ''
                                : chat.sender_company_user?.icon_image || ''
                        }
                        name={
                            chat.sender_agent_user
                                ? getFullUserName(chat.sender_agent_user?.last_name, chat.sender_agent_user?.first_name)
                                : getFullUserName(
                                      chat.sender_company_user?.last_name,
                                      chat.sender_company_user?.first_name,
                                  )
                        }
                        isSelf={chat?.sender_company_user?.id === user.id}
                        date={chat.send_at}
                        message={chat.message}
                    />
                ))}
            </MessageContainer>
            <MessageInputContainer>
                <MessageInput
                    archived={selectedChatRoom.match.archived}
                    message={message}
                    setMessage={setMessage}
                    sendMessage={() => sendMessage()}
                    requesting={loading}
                    showTemplate
                    companyType="company"
                />
            </MessageInputContainer>
            {createModal(
                <SetMeetingDate
                    showRemindMessage={needRemindMessage}
                    meetingDate={selectedChatRoom.match.meeting_date ?? undefined}
                    control={control}
                    errorMsg={errors.meeting_date?.message || ''}
                    onClick={handleSubmit((data) => setMeetingDate(data.meeting_date.replace(/\//g, '-'), closeModal))}
                />,
            )}
            {createArchiveAgentModal(
                <ArchiveAgentModal
                    agentName={selectedChatRoom.match.agent.name}
                    setValue={setArchiveAgentValue}
                    onClick={blockAgent}
                    errorMsg={blockAgentErrors.message?.message || ''}
                    loading={loading}
                />,
            )}
        </Wrapper>
    );
};
